import { DataTable } from "mantine-datatable";
import React, { useEffect, useState } from "react";
import HistoryService from "../../service/history.service";
import Swal from "sweetalert2";
import moment from "moment";

/**
 * Log Registrations — the SEPARATE device-registration log (silent self-loopback
 * verifications). Distinct from Log Messages (delivery tests): different backend
 * endpoint (/admin/registrations) and different table (registration_log). Tokens
 * are shown REG_ tagged. Auto-refreshes every 5s.
 */
function LogRegistrations() {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZES = [10, 20, 30, 50, 100];
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [isFirst, setIsFirst] = useState(false);

  const showMessage = () => {
    const toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      customClass: { container: "toast" },
    });
    toast.fire({ icon: "error", title: "data not found", padding: "10px 20px" });
  };

  const getRegistrations = async () => {
    setIsFirst(true);
    setIsLoading(isFirst ? true : false);
    try {
      const response = await HistoryService.getRegistrations();
      const list = response?.data?.apiresponse?.data || [];
      setData(list);
    } catch (error) {
      showMessage();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRegistrations();
    const intervalId = setInterval(() => {
      setIsFirst(false);
      getRegistrations();
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  // Client-side filter by number + paginate (the endpoint returns the recent list).
  const filtered = mobileNumber
    ? data.filter((r) => String(r.mobileNumber || "").includes(mobileNumber))
    : data;
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <h5 className="text-lg font-semibold dark:text-white-light mb-3">
        Log Registrations
      </h5>

      <div className="allfilter flex items-center justify-end gap-3 flex-wrap mb-4">
        <div className="w-full sm:w-fit">
          <input
            type="number"
            placeholder="Mobile Number"
            value={mobileNumber}
            onChange={(e) => {
              setMobileNumber(e.target.value);
              setPage(1);
            }}
            className="block border border-gray-300 rounded-md p-2 w-full sm:w-54"
          />
        </div>
      </div>

      <DataTable
        noRecordsText="No registrations yet"
        highlightOnHover
        className="whitespace-nowrap table-hover min-w-[780px]"
        records={paged}
        columns={[
          {
            accessor: "id",
            title: "ID",
            render: ({ id }, index) => (
              <span>{(page - 1) * pageSize + index + 1}</span>
            ),
          },
          {
            accessor: "mobileNumber",
            title: "Mobile Number",
            render: ({ mobileNumber }) => (
              <span className="!text-xs">{mobileNumber ? mobileNumber : "--"}</span>
            ),
          },
          {
            accessor: "method",
            title: "Method",
            render: ({ method }) =>
              method ? (
                <span className="inline-flex items-center rounded-md bg-gray-100 dark:bg-[#1b2e4b] text-gray-700 dark:text-gray-300 px-2 py-0.5 text-[11px] font-mono font-semibold tracking-wide">
                  {method}
                </span>
              ) : (
                <span className="text-xs text-gray-400">--</span>
              ),
          },
          {
            accessor: "status",
            title: "Status",
            render: ({ status }) => {
              const s = status || "";
              const cls =
                s === "verified"
                  ? "bg-success/15 text-success border-success/30"
                  : s === "sent" || s === "requested"
                  ? "bg-info/15 text-info border-info/30"
                  : s
                  ? "bg-warning/15 text-warning border-warning/30"
                  : "bg-gray-100 text-gray-500 border-gray-200 dark:bg-[#1b2e4b] dark:border-[#2a2a2d]";
              return (
                <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-semibold capitalize ${cls}`}>
                  {s ? (
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                  ) : null}
                  {s ? s.replace(/_/g, " ") : "--"}
                </span>
              );
            },
          },
          {
            accessor: "token",
            title: "Ref",
            render: ({ token }) => (
              <span className="!text-xs">{token ? token : "--"}</span>
            ),
          },
          {
            accessor: "deviceKeyId",
            title: "Device Key",
            render: ({ deviceKeyId }) => (
              <span className="!text-xs">{deviceKeyId ? deviceKeyId : "--"}</span>
            ),
          },
          {
            accessor: "creationDate",
            title: "Date",
            render: ({ creationDate }) => (
              <span className="!text-xs">
                {creationDate
                  ? moment(Number(creationDate)).format("DD/MM/YYYY HH:mm")
                  : "--"}
              </span>
            ),
          },
        ]}
        totalRecords={filtered.length}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={setPageSize}
        minHeight={200}
        loading={isLoading}
        fetching={isLoading}
      />
    </>
  );
}

export default LogRegistrations;
